import React, { useState } from "react";
import { useLocalization } from "@fluent/react";
import { TextInput } from "@narmi/design_system";
import { ContextForm } from "cerulean";
import Account from "byzantine/src/Account";
import OrgRoleActions from "./OrgRoleActions";
import RoleStartFromField from "./RoleStartFromField";
import RoleAccountsSection from "./RoleAccountsSection";
import RoleFutureAccountsField from "./RoleFutureAccountsField";
import RoleLevelPermissionsSection from "./RoleLevelPermissionsSection";
import RoleDailyLimitsSection from "./RoleDailyLimitsSection";
import styles from "./OrganizationRoles.module.scss";
import {
  validateRoleAccounts,
  validateMaxLength,
  AccountPermissionState,
  RoleFormData,
  copyAccountPermissions,
  validateRoleLimits,
  validateFutureAccounts,
  ROLE_NAME_MAX_LENGTH,
  ROLE_DESCRIPTION_MAX_LENGTH,
} from "./RoleFormUtils";

interface RoleFormBodyProps {
  formData: Record<string, any>;
  onChange: (update: Record<string, any>) => void;
  onAccountPermissionsChange: (
    accountUuid: string,
    next: AccountPermissionState
  ) => void;
  onSubmit: (callback: (errors?: unknown) => void) => void;
  onCancel: () => void;
  isSaving: boolean;
  features?: Record<string, any>;
  isUsingConsumerBillpay?: boolean;
  accounts: Account[];
  externalAccounts: Account[];
  mode: "new" | "edit";
}

/**
 * The role form shared by the new and edit role pages: name and description, accounts with their
 * permissions, role-level permissions, daily limits and the save and cancel actions. Render it
 * inside a `ContextForm`.
 */
export default function RoleFormBody({
  formData,
  onChange,
  onAccountPermissionsChange,
  onSubmit,
  onCancel,
  isSaving,
  features,
  isUsingConsumerBillpay = false,
  accounts,
  externalAccounts,
  mode,
}: RoleFormBodyProps) {
  const { l10n } = useLocalization();
  const isNewRolePage = mode === "new";
  // True while "Start from" loads a role. Saving then would submit the form's previous values.
  const [isSeedingRoleData, setIsSeedingRoleData] = useState(false);

  // The Save button is disabled while seeding, but the form can still be submitted another way
  // (Enter in a text field), so the handler checks too.
  const onSubmitUnlessSeeding = (callback: (errors?: unknown) => void) => {
    if (isSeedingRoleData) {
      callback();
      return;
    }
    onSubmit(callback);
  };

  return (
    <>
      <div className="section-box margin--top--xl">
        <div className="nds-grid nds-span-12">
          <div className="nds-span-6 nds-span-tablet-3 nds-span-mobile-2 nds-input-style-override">
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              {l10n.getString("org-role-field-name")}
            </label>
            <ContextForm.Field
              required
              validate={(value: string) =>
                validateMaxLength(
                  value,
                  ROLE_NAME_MAX_LENGTH,
                  l10n.getString("org-role-error-name-too-long", {
                    max: ROLE_NAME_MAX_LENGTH,
                  })
                )
              }
              style={{ marginBottom: "0px" }}
            >
              <TextInput
                id="role-name"
                field="role_name"
                maxLength={ROLE_NAME_MAX_LENGTH}
              />
            </ContextForm.Field>
          </div>
          <div className="nds-span-6 nds-span-tablet-3 nds-span-mobile-2 nds-input-style-override">
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              {l10n.getString("org-role-field-description")}
            </label>
            <ContextForm.Field
              required
              validate={(value: string) =>
                validateMaxLength(
                  value,
                  ROLE_DESCRIPTION_MAX_LENGTH,
                  l10n.getString("org-role-error-description-too-long", {
                    max: ROLE_DESCRIPTION_MAX_LENGTH,
                  })
                )
              }
              style={{ marginBottom: "0px" }}
            >
              <TextInput
                id="role-description"
                field="role_description"
                maxLength={ROLE_DESCRIPTION_MAX_LENGTH}
              />
            </ContextForm.Field>
          </div>
          {isNewRolePage && (
            <RoleStartFromField
              onChange={onChange}
              accounts={accounts}
              externalAccounts={externalAccounts}
              features={features}
              onSeedingChange={setIsSeedingRoleData}
            />
          )}
        </div>
      </div>
      <hr className={styles.sectionDivider} />
      <ContextForm.Field
        required
        validate={validateRoleAccounts}
        style={{ marginBottom: "0px" }}
      >
        <RoleAccountsSection
          field="accounts"
          onChange={onChange}
          features={features}
          accountPermissions={formData.accountPermissions}
          onAccountPermissionsChange={onAccountPermissionsChange}
          onCopyAccountPermissions={
            isNewRolePage
              ? (sourceAccountId, targetAccountIds) =>
                  onChange(
                    copyAccountPermissions(
                      formData as RoleFormData,
                      sourceAccountId,
                      targetAccountIds
                    )
                  )
              : undefined
          }
        />
      </ContextForm.Field>
      <ContextForm.Field
        validate={validateFutureAccounts}
        style={{ marginBottom: "0px" }}
      >
        <RoleFutureAccountsField
          field="futureAccounts"
          formData={formData as RoleFormData}
        />
      </ContextForm.Field>
      <RoleLevelPermissionsSection
        features={features}
        formData={formData}
        isUsingConsumerBillpay={isUsingConsumerBillpay}
      />
      <ContextForm.Field
        validate={(_: unknown, data: Record<string, any>) =>
          validateRoleLimits(data as RoleFormData, features)
        }
        style={{ marginBottom: "0px" }}
      >
        <RoleDailyLimitsSection
          field="limits"
          features={features}
          formData={formData as RoleFormData}
          onLimitChange={onChange}
        />
      </ContextForm.Field>
      <OrgRoleActions
        onSubmit={onSubmitUnlessSeeding}
        dangerouslyDisableShowLoading
        isDisabled={isSaving || isSeedingRoleData}
        onCancel={onCancel}
      />
    </>
  );
}
